import { useRef } from "react";
import { Space, Descriptions, Typography } from "antd";
import type { DescriptionsProps } from "antd";

import Identity from "@components/Identity";
import { Value } from "@components/Text";
import { AccountUpdateDrawer, useInvestorAccreditation } from "@components/Investor";

import InvestmentErrors from "./InvestmentErrors";
import type { Account, Investor, DrawerHandle } from "../../types";

const LABEL_DOB = "Date of Birth";
const LABEL_ADDRESS = "Mailing Address";
const LABEL_ACCREDITATION = "Accreditation";

const IDENTITY_STYLE = { marginLeft: 3 };

type Props = {
  errors: string[];
  onClick: () => void;
  account: Account;
  investor: Investor;
  isFunded: boolean;
  isSigned: boolean;
  onAfterAccountUpdate: () => void;
  openInvestmentAccreditationUpdateModal: () => void;
};

const InvestmentInvestor = ({
  errors,
  onClick,
  account,
  investor,
  isFunded,
  isSigned,
  onAfterAccountUpdate,
  openInvestmentAccreditationUpdateModal,
}: Props) => {
  const accountUpdateDrawer = useRef<DrawerHandle>({});

  const openAccountUpdateDrawer = () =>
    accountUpdateDrawer.current.open();

  const {
    address,
    dateOfBirth,
    hasAccreditation,
    accreditationExpiresAt,
  } = investor;

  const hasInvestorAccrediation = useInvestorAccreditation(
    hasAccreditation,
    accreditationExpiresAt,
  );

  const items: DescriptionsProps["items"] = [
    {
      key: "address",
      label: LABEL_ADDRESS,
      children: <Value value={address} />,
    },
    {
      key: "dob",
      label: LABEL_DOB,
      children: <Value value={dateOfBirth} />,
    },
    {
      key: "accreditation",
      label: LABEL_ACCREDITATION,
      children: isSigned
        ? (
          <Typography.Link onClick={openInvestmentAccreditationUpdateModal}>
            {hasInvestorAccrediation}
          </Typography.Link>
        )
        : hasInvestorAccrediation,
    },
  ];

  const { id } = account;

  return (
    <Space
      size={0}
      direction="vertical"
      className="descriptions-children"
    >
      <div style={IDENTITY_STYLE}>
        <Identity
          strong
          id={id}
          onClick={onClick}
          hasAvatar={false}
        />
      </div>

      <Descriptions
        size="small"
        items={items}
        layout="vertical"
      />

      <InvestmentErrors
        errors={errors}
        onResolve={openAccountUpdateDrawer}
        isFunded={isFunded}
      />

      <AccountUpdateDrawer
        drawer={accountUpdateDrawer}
        account={account}
        onSuccess={onAfterAccountUpdate}
      />
    </Space>
  );
};

export default InvestmentInvestor;
